"use client"

import React, { useEffect, useState, useMemo, useCallback } from "react"
import { useOutletContext, useParams, useNavigate } from "react-router-dom"
import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート
import { englishStatusLabel, isEnglishOperatorMode, withOperatorSurface } from "../utils/operatorMode";
import {
  getOperatorPrefectureLabelFromAddress,
  getOperatorTaxonomyLabel,
  getOperatorTaxonomyLabelsFromText,
} from "../utils/operatorTaxonomyLabels";

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const getEditListTemplateCopy = (isEnglishBeta) => isEnglishBeta ? {
  editTitle: "Edit Recipient List",
  createTitle: "Create Recipient List",
  nameLabel: "List name",
  descriptionLabel: "Description",
  optionalLabel: "optional",
  searchTitle: "Search Companies to Include",
  clearFilters: "Clear filters",
  searchPlaceholder: "Keyword (company name, URL, etc.)",
  industryFilter: "Industry",
  addressFilter: "Location",
  businessTagsFilter: "Business tags",
  ownHistory: "Your outreach history",
  globalSendHistory: "Shared outreach history",
  globalClickHistory: "Shared click history",
  all: "All",
  successOnly: "Submitted only",
  needsReviewOnly: "Needs review only",
  failedOnly: "Not eligible only",
  unsentOnly: "Not sent only",
  clicked: "Clicked",
  none: "None",
  searching: "Searching...",
  search: "Search",
  selectCompanies: "Select companies",
  selectedSuffix: "selected",
  displayCount: "Rows:",
  selectPage: "Select page",
  clearAll: "Clear all",
  loadingList: "Loading companies...",
  sent: "Sent",
  success: "Submitted",
  needsReview: "Needs review",
  clicks: "Clicks",
  noGlobalRecord: "No shared history",
  industry: "Industry",
  address: "Location",
  businessTags: "Business tags",
  listTags: "List tags",
  selected: "Selected",
  company: "Company",
  url: "URL",
  ownHistoryShort: "Your History",
  sharedHistory: "Shared History",
  noRecord: "No history",
  firstPage: "First",
  previousPage: "Prev",
  nextPage: "Next",
  lastPage: "Last",
  modalSelectedSuffix: "selected",
  cancel: "Cancel",
  applySelection: "Apply selection",
  filterIndustryTitle: "Filter by industry",
  filterAddressTitle: "Filter by location",
  filterBusinessTagsTitle: "Filter by business tags",
  nameRequired: "List name is required.",
  dataLoadFailure: "Could not load company data.",
  saveFailurePrefix: "Save failed: ",
  saveSuccess: "Recipient list saved.",
  limitWarningTitle: "Selection limit exceeded",
  limitWarningBody: (limit, count) => `You can save up to ${limit.toLocaleString()} companies in one recipient list. You currently selected ${count.toLocaleString()} companies.`,
  limitWarningHelp: "Reduce the selected companies or split them into multiple recipient lists.",
  limitAlert: (limit, count) =>
    `You can save up to ${limit.toLocaleString()} companies in one recipient list.\n\n` +
    `Current selection: ${count.toLocaleString()} companies\n\n` +
    "Please split this into multiple lists or reduce the selection.",
  saveButton: "Save list",
  saving: "Saving...",
} : {
  editTitle: "リストテンプレートを編集",
  createTitle: "新規リストテンプレート作成",
  nameLabel: "リスト名",
  descriptionLabel: "説明",
  optionalLabel: "任意",
  searchTitle: "🔍 含める企業を検索",
  clearFilters: "フィルタークリア",
  searchPlaceholder: "フリーワード (会社名、URLなど)",
  industryFilter: "業種",
  addressFilter: "住所",
  businessTagsFilter: "事業タグ",
  ownHistory: "自社送信履歴",
  globalSendHistory: "全体送信履歴",
  globalClickHistory: "全体クリック履歴",
  all: "すべて",
  successOnly: "成功のみ",
  needsReviewOnly: "要確認のみ",
  failedOnly: "失敗のみ",
  unsentOnly: "未送信のみ",
  clicked: "クリックあり",
  none: "なし",
  searching: "検索中...",
  search: "検索",
  selectCompanies: "含める企業を選択",
  selectedSuffix: "件選択中",
  displayCount: "表示件数:",
  selectPage: "このページを全選択",
  clearAll: "全解除",
  loadingList: "営業リストを読み込んでいます...",
  sent: "送信",
  success: "成功",
  needsReview: "要確認",
  clicks: "クリック",
  noGlobalRecord: "全体実績なし",
  industry: "業種",
  address: "住所",
  businessTags: "事業タグ",
  listTags: "リストタグ",
  selected: "選択",
  company: "会社名",
  url: "URL",
  ownHistoryShort: "自社履歴",
  sharedHistory: "全体履歴",
  noRecord: "（実績なし）",
  firstPage: "最初",
  previousPage: "前へ",
  nextPage: "次へ",
  lastPage: "最後",
  modalSelectedSuffix: "件 選択中",
  cancel: "キャンセル",
  applySelection: "選択を反映",
  filterIndustryTitle: "業種で絞り込み",
  filterAddressTitle: "住所で絞り込み",
  filterBusinessTagsTitle: "事業タグで絞り込み",
  nameRequired: "リスト名は必須です。",
  dataLoadFailure: "データの読み込み中にエラーが発生しました。",
  saveFailurePrefix: "保存に失敗しました: ",
  saveSuccess: null,
  limitWarningTitle: "上限超過警告",
  limitWarningBody: (limit, count) => `保存できる企業数の上限（${limit.toLocaleString()}件）を超えています。現在の選択数は${count.toLocaleString()}件です。`,
  limitWarningHelp: "このままでは保存できません。選択件数を減らしてください。",
  limitAlert: (limit, count) =>
    `保存できる企業数の上限（${limit.toLocaleString()}件）を超えています。\n\n` +
    `現在の選択数: ${count.toLocaleString()}件\n\n` +
    `お手数ですが、複数のリストに分割して保存するか、選択件数を減らしてください。`,
  saveButton: "この内容で保存",
  saving: "保存中...",
};

const formatCountWithSuffix = (count, suffix, isEnglishBeta) =>
  isEnglishBeta ? `${count.toLocaleString()} ${suffix}` : `${count.toLocaleString()}${suffix}`;

const formatTaxonomyLabel = (value, type, isEnglishBeta) => {
  const text = String(value || "").trim();
  if (!isEnglishBeta || !text) return text;
  const label = getOperatorTaxonomyLabel(text, type);
  return label ? `${label} (${text})` : text;
};

const formatTaxonomyText = (value, type, isEnglishBeta) => {
  const text = String(value || "").trim();
  if (!isEnglishBeta || !text) return text;
  const labels = getOperatorTaxonomyLabelsFromText(text, type);
  return labels.length ? `${labels.join(", ")} (${text})` : text;
};

const formatAddressText = (value, isEnglishBeta) => {
  const text = String(value || "").trim();
  if (!isEnglishBeta || !text) return text;
  const prefectureLabel = getOperatorPrefectureLabelFromAddress(text);
  return prefectureLabel ? `${prefectureLabel} (${text})` : text;
};

function renderMyHistoryStatus(status, isEnglishBeta = false) {
  const normalizedStatus = String(status || "").toLowerCase();

  if (!normalizedStatus) {
    return <span className="text-gray-400">{isEnglishBeta ? "Not sent" : "未送信"}</span>;
  }

  if (isEnglishBeta) {
    const label = englishStatusLabel(status);
    if (normalizedStatus === "success") {
      return <span className="text-green-500 font-bold">{label}</span>;
    }
    if (normalizedStatus.includes("review")) {
      return <span className="text-yellow-500 font-bold">{label}</span>;
    }
    return <span className="text-red-500 font-bold">{label}</span>;
  }

  if (normalizedStatus === "success") {
    return <span className="text-green-500 font-bold">✅ 成功</span>;
  }

  if (normalizedStatus.includes("review")) {
    return <span className="text-yellow-500 font-bold">⚠️ 要確認</span>;
  }

  if (
    normalizedStatus.startsWith("fail") ||
    ["no_form_found", "dead_url", "wrong_form_type", "sales_rejected"].includes(normalizedStatus)
  ) {
    return <span className="text-red-500 font-bold">❌ 失敗</span>;
  }

  return <span className="text-red-500 font-bold">❌ 失敗</span>;
}

// --- SVG Icons ---
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const LoadingSpinner = () => (
  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// --- Helper & UI Components ---
function Pagination({ currentPage, totalPages, onPageChange, isLoading, copy }) {
  if (totalPages <= 1) return null
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <button onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.firstPage}</button>
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.previousPage}</button>
      <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.nextPage}</button>
      <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || isLoading} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.lastPage}</button>
    </div>
  )
}

const FilterModal = ({ title, options, selected, onSave, onClose, isAddress = false, copy, isEnglishBeta = false, taxonomyType = "" }) => {
    const [currentSelection, setCurrentSelection] = useState(selected);
    const renderOptionLabel = (value, type = taxonomyType) => formatTaxonomyLabel(value, type, isEnglishBeta);

    const handleCheckboxChange = (group, value) => {
        setCurrentSelection(prev => {
            const newGroupSelection = prev[group] ? [...prev[group]] : [];
            if (newGroupSelection.includes(value)) {
                const filteredGroup = newGroupSelection.filter(item => item !== value);
                const newSelection = { ...prev, [group]: filteredGroup };
                if (filteredGroup.length === 0) {
                    delete newSelection[group];
                }
                return newSelection;
            } else {
                return { ...prev, [group]: [...newGroupSelection, value] };
            }
        });
    };

    const handleGroupCheckboxChange = (group, values) => {
        setCurrentSelection(prev => {
            const newSelection = { ...prev };
            const currentGroupValues = newSelection[group] || [];
            const allValuesInGroup = values.map(v => v.name || v);
            const areAllSelected = allValuesInGroup.every(v => currentGroupValues.includes(v));

            if (areAllSelected) {
                delete newSelection[group];
            } else {
                newSelection[group] = allValuesInGroup;
            }
            return newSelection;
        });
    };
    
    const handleSimpleCheckboxChange = (value) => {
        setCurrentSelection(prev => {
            const newSelection = [...prev];
            if (newSelection.includes(value)) {
                return newSelection.filter(item => item !== value);
            } else {
                return [...newSelection, value];
            }
        });
    };

    const handleSimpleGroupChange = (groupValues) => {
         setCurrentSelection(prev => {
            const areAllSelected = groupValues.every(v => prev.includes(v));
            if (areAllSelected) {
                return prev.filter(item => !groupValues.includes(item));
            } else {
                return [...new Set([...prev, ...groupValues])];
            }
        });
    };

    const totalSelectedCount = isAddress
        ? Object.values(currentSelection).reduce((acc, curr) => acc + curr.length, 0)
        : currentSelection.length;

    const renderOptions = () => {
        if (isAddress) {
            return Object.entries(options).map(([region, prefectures]) => (
                <div key={region} className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-2">{renderOptionLabel(region, "region")}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
                        {Object.entries(prefectures).map(([pref, cities]) => (
                            <div key={pref}>
                                <div className="font-bold text-indigo-700 mb-1 flex items-center">
                                    <input
                                        type="checkbox"
                                        id={`group-${pref}`}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={cities.length > 0 && cities.every(city => (currentSelection[pref] || []).includes(city))}
                                        onChange={() => handleGroupCheckboxChange(pref, cities)}
                                    />
                                    <label htmlFor={`group-${pref}`} className="ml-2">{renderOptionLabel(pref, "prefecture")}</label>
                                </div>
                                <div className="pl-5 space-y-1">
                                    {cities.map(city => (
                                        <div key={city} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`address-${city}-${pref}`}
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                checked={(currentSelection[pref] || []).includes(city)}
                                                onChange={() => handleCheckboxChange(pref, city)}
                                            />
                                            <label htmlFor={`address-${city}-${pref}`} className="ml-2 text-sm">{city}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ));
        }
        return Object.entries(options).map(([category, values]) => (
            <div key={category} className="mb-4">
                <h4 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-2 flex items-center">
                     <input
                        type="checkbox"
                        id={`group-${category}`}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={values.every(v => currentSelection.includes(v))}
                        onChange={() => handleSimpleGroupChange(values)}
                    />
                    <label htmlFor={`group-${category}`} className="ml-2">{renderOptionLabel(category)}</label>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {values.map(value => (
                        <div key={value} className="flex items-center">
                            <input
                                type="checkbox"
                                id={`val-${value}`}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                checked={currentSelection.includes(value)}
                                onChange={() => handleSimpleCheckboxChange(value)}
                            />
                            <label htmlFor={`val-${value}`} className="ml-2 text-sm">{renderOptionLabel(value)}</label>
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-stretch z-50 p-0 sm:items-center sm:p-4">
            <div className="bg-white rounded-none shadow-2xl w-full max-w-5xl h-full max-h-none flex flex-col sm:max-h-[90vh] sm:rounded-lg">
                <header className="p-4 border-b flex justify-between items-center flex-shrink-0">
                    <h3 className="text-xl font-bold text-gray-800">{title}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">&times;</button>
                </header>
                <main className="p-4 overflow-y-auto flex-grow sm:p-6">
                    {renderOptions()}
                </main>
                <footer className="p-4 border-t flex flex-col gap-3 bg-gray-50 flex-shrink-0 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-indigo-600 font-semibold">{formatCountWithSuffix(totalSelectedCount, copy.modalSelectedSuffix, isEnglishBeta)}</span>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                        <button onClick={onClose} className="py-2 px-4 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold">{copy.cancel}</button>
                        <button onClick={() => onSave(currentSelection)} className="py-2 px-4 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 font-semibold">{copy.applySelection}</button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

// --- Main Component ---
function EditListTemplatePage({ templateId = undefined, returnPath = null }) {
  const { user } = useOutletContext();
  const { id: routeId } = useParams();
  const id = templateId === undefined ? routeId : templateId;
  const navigate = useNavigate();
  const resolvedReturnPath = returnPath || withOperatorSurface("/list-templates");
  const isEnglishBeta = isEnglishOperatorMode();
  const copy = useMemo(() => getEditListTemplateCopy(isEnglishBeta), [isEnglishBeta]);
  const clientId = useMemo(() => (user?.role === "admin" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);

  // ★★★ 修正点①: 上限数を20,000件に変更 ★★★
  const LIST_SAVE_LIMIT = 20000;

  // --- State ---
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [salesList, setSalesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  
  // ★★★ 修正点②: 新しいフィルターUIとロジックを導入 ★★★
  const [filterOptions, setFilterOptions] = useState({ industry: {}, business_tags: {}, addresses: {} });
  const [isModalOpen, setIsModalOpen] = useState({ industry: false, address: false, business_tags: false });
  
  const initialFilters = {
    industry_class: [], address: {}, business_tags: [], list_tags: [],
    searchTerm: "", sentStatus: "all", globalClickStatus: "all",
    minGlobalSends: "", globalSendStatus: "all",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [activeFilters, setActiveFilters] = useState(initialFilters);
 
  // --- Data Fetching ---
  const fetchSalesList = useCallback(async (page, currentFilters) => {
      if (!clientId) return;

      setIsLoading(true);
      const payload = {
        client_id: clientId,
        page,
        limit: itemsPerPage,
        filters: currentFilters,
        listSourceType: 'master' 
      };

      try {
        const res = await fetch(`${API_URL}/api/load`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader(), // ★ 2. 認証ヘッダーを追加
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) {
          setSalesList(data.data || []);
          setPagination(data.pagination || null);
        } else {
          console.error("営業リストの読み込みに失敗:", data.error);
        }
      } catch (err) {
        console.error("API通信エラー:", err);
        alert(copy.dataLoadFailure);
      } finally {
        setIsLoading(false);
      }
    }, [clientId, itemsPerPage, copy.dataLoadFailure]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      fetch(`${API_URL}/api/saved-lists/${id}`, {
        headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setName(data.list.name);
            setDescription(data.list.description || "");
            setSelectedIds(new Set(data.list.target_ids));
          }
        })
        .finally(() => setIsLoading(false));
    }
    // ★★★ 修正点③: フィルター選択肢の取得APIを最新化 ★★★
    Promise.all([
        fetch(`${API_URL}/api/search-filters`, { headers: getAuthHeader() }).then(res => res.json()), // ★ 2. 認証ヘッダーを追加
        fetch(`${API_URL}/api/address-master`, { headers: getAuthHeader() }).then(res => res.json())  // ★ 2. 認証ヘッダーを追加
    ]).then(([searchFiltersData, addressData]) => {
        if(searchFiltersData.success) {
            setFilterOptions({
                industry: searchFiltersData.filters['業種'] || {},
                business_tags: searchFiltersData.filters['事業タグ'] || {},
            });
        }
        if(addressData.success) {
            setFilterOptions(prev => ({...prev, addresses: addressData.data || {}}));
        }
    }).catch(err => console.error("Filter options fetch error:", err));
  }, [id, clientId, fetchSalesList]);

  // currentPage, activeFiltersの変更を監視して再取得
  useEffect(() => {
    fetchSalesList(currentPage, activeFilters, 'master');
  }, [currentPage, activeFilters, fetchSalesList, id]);

  // --- Event Handlers ---
  const handleModalSave = (filterName, selection) => {
    setFilters(prev => ({ ...prev, [filterName]: selection }));
    setIsModalOpen(prev => ({ ...prev, [filterName]: false }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleSearch = () => {
    setCurrentPage(1);
    setActiveFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setActiveFilters(initialFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && pagination && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = Number(e.target.value);
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  const handleCheckboxChange = (companyId, isChecked) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (isChecked) newSet.add(companyId);
      else newSet.delete(companyId);
      return newSet;
    });
  };

  const handleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      salesList.forEach((item) => newSet.add(item.id));
      return newSet;
    });
  };

  const handleClearAll = () => {
    setSelectedIds(new Set());
  };

  const handleSave = async () => {
    if (!name) {
      alert(copy.nameRequired);
      return;
    }

    if (selectedIds.size > LIST_SAVE_LIMIT) {
      alert(copy.limitAlert(LIST_SAVE_LIMIT, selectedIds.size));
      return;
    }
    
    setIsLoading(true);
    const payload = { client_id: clientId, name, description, target_ids: Array.from(selectedIds) };
    const url = id ? `${API_URL}/api/saved-lists/${id}` : `${API_URL}/api/saved-lists`;
    const method = id ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(), // ★ 2. 認証ヘッダーを追加
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      alert(copy.saveSuccess || data.message);
      navigate(resolvedReturnPath);
    } catch (error) {
      alert(`${copy.saveFailurePrefix}${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const isOverLimit = selectedIds.size > LIST_SAVE_LIMIT;
  
  const countSelected = (filterName) => {
     if (filterName === 'address') {
         return Object.values(filters.address).reduce((sum, cities) => sum + cities.length, 0);
     }
     return filters[filterName]?.length || 0;
  }

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      {isModalOpen.industry && <FilterModal title={copy.filterIndustryTitle} options={filterOptions.industry} selected={filters.industry_class} onSave={(selection) => handleModalSave('industry_class', selection)} onClose={() => setIsModalOpen(p => ({...p, industry: false}))} copy={copy} isEnglishBeta={isEnglishBeta} taxonomyType="industry" />}
      {isModalOpen.address && <FilterModal title={copy.filterAddressTitle} options={filterOptions.addresses} selected={filters.address} onSave={(selection) => handleModalSave('address', selection)} onClose={() => setIsModalOpen(p => ({...p, address: false}))} isAddress={true} copy={copy} isEnglishBeta={isEnglishBeta}/>}
      {isModalOpen.business_tags && <FilterModal title={copy.filterBusinessTagsTitle} options={filterOptions.business_tags} selected={filters.business_tags} onSave={(selection) => handleModalSave('business_tags', selection)} onClose={() => setIsModalOpen(p => ({...p, business_tags: false}))} copy={copy} isEnglishBeta={isEnglishBeta} taxonomyType="businessTag" />}
      
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">
        {id ? copy.editTitle : copy.createTitle}
      </h1>

      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium">
              {copy.nameLabel} <span className="text-red-500">*</span>
            </label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 p-2 border rounded-md"/>
          </div>
          <div>
            <label className="block text-sm font-medium">{copy.descriptionLabel}{isEnglishBeta ? ` (${copy.optionalLabel})` : `（${copy.optionalLabel}）`}</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full mt-1 p-2 border rounded-md"/>
          </div>
        </div>
      </div>

      {/* ★★★ 修正点④: 新しいフィルターUIをここから挿入 ★★★ */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
        <div className="flex flex-col justify-between gap-3 mb-4 sm:flex-row sm:items-center">
            <h2 className="text-xl font-semibold text-gray-700">{copy.searchTitle}</h2>
            <button onClick={clearFilters} className="w-full px-4 py-2 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors sm:w-auto">{copy.clearFilters}</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <input type="text" name="searchTerm" placeholder={copy.searchPlaceholder} value={filters.searchTerm} onChange={handleFilterChange} className="p-2 border rounded-md w-full md:col-span-2"/>
            
            <button onClick={() => setIsModalOpen(p => ({...p, industry: true}))} className="p-2 border rounded-md text-left w-full hover:bg-gray-50 relative">{copy.industryFilter}... <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{countSelected('industry_class')}</span></button>
            <button onClick={() => setIsModalOpen(p => ({...p, address: true}))} className="p-2 border rounded-md text-left w-full hover:bg-gray-50 relative">{copy.addressFilter}... <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{countSelected('address')}</span></button>
            <button onClick={() => setIsModalOpen(p => ({...p, business_tags: true}))} className="p-2 border rounded-md text-left w-full hover:bg-gray-50 relative">{copy.businessTagsFilter}... <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{countSelected('business_tags')}</span></button>
            
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">{copy.ownHistory}</label>
              <select name="sentStatus" value={filters.sentStatus} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white">
                  <option value="all">{copy.all}</option><option value="success">{copy.successOnly}</option><option value="needs_review">{copy.needsReviewOnly}</option><option value="fail">{copy.failedOnly}</option><option value="unsent">{copy.unsentOnly}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">{copy.globalSendHistory}</label>
              <select name="globalSendStatus" value={filters.globalSendStatus} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white">
                  <option value="all">{copy.all}</option><option value="success">{copy.successOnly}</option><option value="fail">{copy.failedOnly}</option><option value="needs_review">{copy.needsReviewOnly}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">{copy.globalClickHistory}</label>
              <select name="globalClickStatus" value={filters.globalClickStatus} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white">
                  <option value="all">{copy.all}</option><option value="yes">{copy.clicked}</option><option value="no">{copy.none}</option>
              </select>
            </div>
        </div>
        
        <div className="mt-4 pt-4 border-t flex justify-end">
          <button onClick={handleSearch} disabled={isLoading} className="flex w-full items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-wait sm:w-auto">
            {isLoading ? <><LoadingSpinner /> {copy.searching}</> : <><SearchIcon /> {copy.search}</>}
          </button>
        </div>
      </div>


      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <h2 className={`font-semibold ${isOverLimit ? 'text-red-600' : 'text-gray-700'}`}>
            {copy.selectCompanies} ({selectedIds.size.toLocaleString()} / {LIST_SAVE_LIMIT.toLocaleString()} {copy.selectedSuffix})
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div>
              <label className="text-sm mr-2">{copy.displayCount}</label>
              <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="p-1 border rounded-md text-sm">
                <option value={100}>100</option><option value="500">500</option><option value="1000">1000</option><option value="2000">2000</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={handleSelectAllOnPage} className="bg-gray-200 text-sm py-2 px-3 rounded-md hover:bg-gray-300">{copy.selectPage}</button>
              <button onClick={handleClearAll} className="bg-gray-200 text-sm py-2 px-3 rounded-md hover:bg-gray-300">{copy.clearAll}</button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center"><div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div><p className="mt-2 text-gray-600">{copy.loadingList}</p></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <div className="divide-y divide-gray-100 md:hidden">
	                {salesList.map((item) => {
	                  const myHistoryIcon = renderMyHistoryStatus(item.my_latest_status, isEnglishBeta)
	                  const hasGlobalSent = Number(item.global_sent_count || 0) > 0
	                  const hasGlobalSuccess = Number(item.global_success_count || 0) > 0
	                  const hasGlobalReview = Number(item.global_review_count || 0) > 0
	                  const hasGlobalClick = Number(item.global_click_count || 0) > 0
	                  return (
                    <div key={`${item.id}-mobile`} className="p-4">
                      <div className="flex items-start gap-3">
                        <input type="checkbox" checked={selectedIds.has(item.id)} onChange={(e) => handleCheckboxChange(item.id, e.target.checked)} className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 break-words">{item.company || "---"}</p>
                          <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-1 block text-xs text-blue-600 hover:underline break-all">{item.url || "---"}</a>
                        </div>
                      </div>
	                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
	                        <span className="rounded-full bg-gray-100 px-2 py-1">{myHistoryIcon}</span>
	                        {hasGlobalSent ? (
	                          <>
	                            <span title={copy.sent} aria-label={copy.sent} className="rounded-full bg-gray-100 px-2 py-1">📨</span>
	                            {hasGlobalSuccess && <span title={copy.success} aria-label={copy.success} className="rounded-full bg-green-100 px-2 py-1 text-green-800">✅</span>}
	                            {hasGlobalReview && <span title={copy.needsReview} aria-label={copy.needsReview} className="rounded-full bg-yellow-100 px-2 py-1 text-yellow-800">⚠️</span>}
	                            {hasGlobalClick && <span title={copy.clicks} aria-label={copy.clicks} className="rounded-full bg-blue-100 px-2 py-1 text-blue-800">🖱️</span>}
	                          </>
	                        ) : (
	                          <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-500">{copy.noGlobalRecord}</span>
                        )}
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-1 text-xs text-gray-600">
                        <p><span className="font-semibold text-gray-800">{copy.industry}:</span> {formatTaxonomyText(item.industry_class, "industry", isEnglishBeta) || "---"}</p>
                        <p><span className="font-semibold text-gray-800">{copy.address}:</span> {formatAddressText(item.address, isEnglishBeta) || "---"}</p>
                        {item.business_tags && <p><span className="font-semibold text-gray-800">{copy.businessTags}:</span> {formatTaxonomyText(item.business_tags, "businessTag", isEnglishBeta)}</p>}
                        {item.list_tags && <p><span className="font-semibold text-gray-800">{copy.listTags}:</span> {item.list_tags}</p>}
                      </div>
                    </div>
                  )
                })}
              </div>
              <table className="hidden w-full text-sm md:table">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.selected}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.company}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.url}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.ownHistoryShort}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.sharedHistory}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.industry}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.address}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.businessTags}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.listTags}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
	                  {salesList.map((item) => {
	                    const myHistoryIcon = renderMyHistoryStatus(item.my_latest_status, isEnglishBeta)
	                    const industryText = formatTaxonomyText(item.industry_class, "industry", isEnglishBeta)
	                    const addressText = formatAddressText(item.address, isEnglishBeta)
	                    const businessTagsText = formatTaxonomyText(item.business_tags, "businessTag", isEnglishBeta)
	                    const hasGlobalSent = Number(item.global_sent_count || 0) > 0
	                    const hasGlobalSuccess = Number(item.global_success_count || 0) > 0
	                    const hasGlobalReview = Number(item.global_review_count || 0) > 0
	                    const hasGlobalClick = Number(item.global_click_count || 0) > 0
	                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input type="checkbox" checked={selectedIds.has(item.id)} onChange={(e) => handleCheckboxChange(item.id, e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"/>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={item.company}>{item.company || "---"}</div></td>
                        <td className="px-4 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 truncate max-w-xs" title={item.url}>{item.url ? (<a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{item.url}</a>) : ("---")}</div></td>
                        <td className="px-4 py-4 whitespace-nowrap">{myHistoryIcon}</td>
	                        <td className="px-4 py-4 whitespace-nowrap">
	                          {hasGlobalSent ? (
	                            <div className="flex items-center flex-wrap gap-1 text-xs">
	                              <span title={copy.sent} aria-label={copy.sent} className="bg-gray-200 px-2 py-0.5 rounded-full whitespace-nowrap">📨</span>
	                              {hasGlobalSuccess && (<span title={copy.success} aria-label={copy.success} className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full whitespace-nowrap">✅</span>)}
	                              {hasGlobalReview && (<span title={copy.needsReview} aria-label={copy.needsReview} className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full whitespace-nowrap">⚠️</span>)}
	                              {hasGlobalClick && (<span title={copy.clicks} aria-label={copy.clicks} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full whitespace-nowrap">🖱️</span>)}
	                            </div>
	                          ) : (<span className="text-gray-400 text-xs">{copy.noRecord}</span>)}
	                        </td>
                        <td className="px-4 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 truncate max-w-xs" title={industryText || item.industry_class}>{industryText || "---"}</div></td>
                        <td className="px-4 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 truncate max-w-xs" title={addressText || item.address}>{addressText || "---"}</div></td>
                        <td className="px-4 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 truncate max-w-xs" title={businessTagsText || item.business_tags}>{businessTagsText || "---"}</div></td>
                        <td className="px-4 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 truncate max-w-xs" title={item.list_tags}>{item.list_tags || "---"}</div></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="p-2 border-t"><Pagination currentPage={currentPage} totalPages={pagination ? pagination.totalPages : 0} onPageChange={handlePageChange} isLoading={isLoading} copy={copy} /></div>
          </>
        )}
      </div>

      {isOverLimit && (
        <div className="mt-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-md">
            <h3 className="font-bold">{copy.limitWarningTitle}</h3>
            <p>{copy.limitWarningBody(LIST_SAVE_LIMIT, selectedIds.size)}</p>
            <p className="mt-1 text-sm">{copy.limitWarningHelp}</p>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-3 sm:flex sm:justify-end sm:gap-4">
        <button onClick={() => navigate(resolvedReturnPath)} className="bg-gray-200 py-2 px-4 rounded-md hover:bg-gray-300">{copy.cancel}</button>
        <button onClick={handleSave} disabled={isLoading || isOverLimit} className="bg-blue-600 text-white font-semibold py-2 px-6 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-700">{isLoading ? copy.saving : copy.saveButton}</button>
      </div>
    </div>
  )
}

export default EditListTemplatePage
